import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/lib/api';

export type UserRole =
  | 'super_admin' | 'admin' | 'sales_manager' | 'sales_agent' | 'accountant' | 'viewer';

export type User = {
  id: string;
  username: string;
  email: string | null;
  fullName: string;
  phone: string;
  role: UserRole;
  active: boolean;
  twoFactorEnabled: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CreateUserInput = {
  username: string;
  email?: string;
  fullName: string;
  phone: string;
  password: string;
  role: UserRole;
};

export type UpdateUserInput = {
  email?: string | null;
  fullName?: string;
  phone?: string;
  role?: UserRole;
  active?: boolean;
};

export type UserDetail = User & { userProjects: { projectId: string }[] };

export function useUser(id: string | undefined) {
  return useQuery({
    queryKey: ['user', id],
    enabled: !!id,
    queryFn: async () => {
      const res = await api.get<{ data: UserDetail }>(`/users/${id}`);
      return res.data.data;
    },
  });
}

export function useSetUserProjects() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, projectIds }: { id: string; projectIds: string[] }) => {
      await api.post(`/users/${id}/projects`, { projectIds });
    },
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      void qc.invalidateQueries({ queryKey: ['user', id] });
    },
  });
}

export function useUsersList(params?: { search?: string; role?: UserRole; active?: boolean }) {
  return useQuery({
    queryKey: ['users', params],
    queryFn: async () => {
      const res = await api.get<{ data: User[]; pagination: { hasMore: boolean } }>('/users', { params });
      return res.data;
    },
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateUserInput) => {
      const res = await api.post<{ data: User }>('/users', input);
      return res.data.data;
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['users'] }); },
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateUserInput }) => {
      const res = await api.patch<{ data: User }>(`/users/${id}`, input);
      return res.data.data;
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['users'] }); },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async ({ id, newPassword }: { id: string; newPassword: string }) => {
      await api.post(`/users/${id}/password`, { newPassword });
    },
  });
}

export function useDeactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/users/${id}`); },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['users'] }); },
  });
}
